/**
 * DTX — announce marquee via Web Animations API.
 * Avoids CSS keyframes (broken when prefers-reduced-motion is on).
 */
(function () {
  if (window.__dtxAnnounceMarqueeBound) return;
  window.__dtxAnnounceMarqueeBound = true;

  const SELECTOR = '[data-dtx-announce]';
  const SPEED_DESKTOP = 56;
  const SPEED_MOBILE = 44;
  const instances = new WeakMap();

  function rebuild(marquee, original) {
    marquee.querySelectorAll('.announce__group').forEach((group) => {
      if (group !== original) group.remove();
    });

    const groupWidth = original.offsetWidth;
    if (!groupWidth) return 0;

    const viewportWidth = Math.max(window.innerWidth, marquee.parentElement?.clientWidth || 0);
    const perHalf = Math.max(1, Math.ceil(viewportWidth / groupWidth));
    const total = perHalf * 2;

    for (let i = 1; i < total; i++) {
      const clone = original.cloneNode(true);
      clone.removeAttribute('data-announce-group');
      clone.setAttribute('aria-hidden', 'true');
      marquee.appendChild(clone);
    }

    return groupWidth * perHalf;
  }

  function startScroll(marquee, halfWidth) {
    marquee.getAnimations().forEach((animation) => animation.cancel());
    marquee.style.animation = 'none';
    marquee.style.transform = 'translate3d(0,0,0)';

    if (!halfWidth) return null;

    const speed = window.innerWidth < 768 ? SPEED_MOBILE : SPEED_DESKTOP;
    const duration = Math.max(8000, (halfWidth / speed) * 1000);

    return marquee.animate(
      [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(-50%,0,0)' }],
      { duration, iterations: Infinity, easing: 'linear' }
    );
  }

  function initAnnounce(root) {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;

    const marquee = root.querySelector('[data-marquee]');
    const original =
      root.querySelector('[data-announce-group]') || root.querySelector('.announce__group');
    if (!marquee || !original) return;

    let active = null;

    const run = () => {
      const halfWidth = rebuild(marquee, original);
      active = startScroll(marquee, halfWidth);
    };

    requestAnimationFrame(() => {
      run();
      if (!active) window.setTimeout(run, 150);
    });

    const finePointer = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
    const onEnter = () => {
      if (active && finePointer()) active.pause();
    };
    const onLeave = () => {
      if (active && active.playState === 'paused') active.play();
    };

    root.addEventListener('pointerenter', onEnter);
    root.addEventListener('pointerleave', onLeave);

    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(run, 200);
    };
    window.addEventListener('resize', onResize, { passive: true });

    instances.set(root, () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      root.removeEventListener('pointerenter', onEnter);
      root.removeEventListener('pointerleave', onLeave);
      active?.cancel();
      instances.delete(root);
    });
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(initAnnounce);
  }

  function unmountAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach((root) => {
      const destroy = instances.get(root);
      if (typeof destroy === 'function') destroy();
    });
  }

  const start = () => mountAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) mountAll(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) unmountAll(event.target);
  });
})();
